'use client';

import * as React from 'react';

import { Dashboard } from '@/shared/i18n';
import { ContentsSection } from '../../shared';
import { initialPagination } from 'src/modules/dashboard/utils/pagination';
import { DashboardEventKeys, DashboardEvents } from 'src/modules/dashboard/events';
import { EventBus } from '@/shared/utils';
import { useDashboardStore } from 'src/modules/dashboard/store/dashboard-store';
import { routes } from 'src/shared/utils/routes';
import { getVideoCompletionPercentage } from 'src/modules/dashboard/utils/return-video-completion-percentage';

interface LessonsWrapperProps {
  dict: Dashboard;
  eventBus: EventBus<DashboardEvents>;
}

const LessonsWrapper: React.FC<LessonsWrapperProps> = ({ dict, eventBus }) => {
  const { favouriteLessons } = useDashboardStore();

  React.useEffect(() => {
    if (!favouriteLessons?.data?.data)
      eventBus.emit(DashboardEventKeys.favouriteLessons.view, initialPagination);
  }, [favouriteLessons?.data?.data, eventBus]);

  return (
    <ContentsSection
      type="slider"
      title={dict.dashboard.favourites.lessons_section}
      isLoading={favouriteLessons.status.isLoading}
      isError={favouriteLessons.status.isError}
      fetchMore={(page, pageSize) =>
        eventBus.emit(DashboardEventKeys.favouriteLessons.view, { page, pageSize })
      }
      pagination={favouriteLessons?.data?.pagination}
      data={
        favouriteLessons?.data?.data?.map((el) => {
          const isWatchingLesson =
            el?.lessonProgresses?.progress_status?.toLowerCase() === 'completed' ||
            el?.lessonProgresses?.progress_status?.toLowerCase() === 'in_progress';

          const lessonProgress =
            el?.lessonProgresses?.progress_status === 'completed'
              ? el?.duration || 0
              : el?.lessonProgresses?.current_second || 0;

          const videoCompletionPercentage = getVideoCompletionPercentage(
            el?.duration || 0,
            lessonProgress,
          );
          return {
            documentId: el?.favouriteDocumentId,
            courseDocumentId: el?.courseDocumentId,
            favouriteDocumentId: el?.favouriteDocumentId,
            lessonDocumentId: el?.lessonDocumentId,
            title: el?.title,
            isCourseStarted: videoCompletionPercentage >= 0 && isWatchingLesson,
            imageSrc: el?.thumbnail?.url || '/assets/images/video-play.svg',
            courseType: el?.courseType,
            seriesName: el?.courseTitle || '',
            coursePercentage: videoCompletionPercentage || 0,
            progressValue: videoCompletionPercentage || 0,
          };
        }) || []
      }
      onCardClickCallback={(item) => {
        if (item.courseType?.toLowerCase() === 'masterclass') {
          eventBus.emit(DashboardEventKeys.redirects.to, {
            url: routes.masterclass.makeUrl(item.courseDocumentId || '', item?.seriesName || ''),
            isExternalPage: false,
          });
          return;
        }
        if (item.courseType?.toLowerCase() === 'percorsi') {
          eventBus.emit(DashboardEventKeys.redirects.to, {
            url: routes.learningPathLesson.makeUrl(
              item.courseDocumentId || '',
              item?.seriesName || '',
              item?.title || '',
              item?.lessonDocumentId || '',
            ),
            isExternalPage: false,
          });
          return;
        }
        if (item.courseType?.toLowerCase() === 'serie') {
          eventBus.emit(DashboardEventKeys.redirects.to, {
            url: routes.serieLesson.makeUrl(
              item.courseDocumentId || '',
              item?.seriesName || '',
              item?.title || '',
              item?.lessonDocumentId || '',
            ),
            isExternalPage: false,
          });
          return;
        }
      }}
      hasNavigationButtons={
        !!favouriteLessons?.data?.data?.length && favouriteLessons?.data?.data?.length > 3
      }
      dict={dict}
      sliderBackgropColor="#15151a"
      eventBus={undefined}
    />
  );
};

export { LessonsWrapper };
